import React, { useEffect, useRef, useState } from "react";
import { useStores } from "../../../use-stores";
import { useSearchParams, useNavigate } from "react-router-dom";

import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";

import { SortType } from "../../../models/SortType";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import FormTextField from "../../components/common/form/FormTextField";
import AppButton from "../../components/common/buttons/AppButton";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";
import {
  parseQueryParamsForVoucherFilters,
  VoucherFilters,
} from "../../../models/Vouchers/VoucherFilters";
import { VoucherStore } from "../../../stores/VoucherStore";
import VouchersTable from "../../components/vouchers/VouchersTable";
import { FilterValue } from "../../../models/Filters/FilterValue";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";

const voucherStore = new VoucherStore();

const ACTION_OPTIONS: FilterValue[] = [
  { id: "download_vouchers", value: "Download Vouchers" },
  { id: "sync_all_brands", value: "Sync All Brands" },
];

const VouchersListing: React.FC = () => {
  const theme = useTheme();

  let navigate = useNavigate();

  const { userStore, orderStore, customerStore } = useStores();

  const { showError, showSuccess } = useNotification();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters: VoucherFilters = parseQueryParamsForVoucherFilters(
    null,
    searchParams
  );

  const [filters, setFilters] = useState<VoucherFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<VoucherFilters>(initialFilters);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);

  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<HTMLElement | null>(null);

  let apiTimer: any;

  useEffect(() => {
    fetchVouchers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [filters]);

  useEffect(() => {
    fetchVouchers();
  }, [selectedFilters]);

  const handleSearchBar = async (searchText: string | undefined) => {
    if (searchText && searchText.length !== 0) {
      setSearchTerm(searchText);
      setFilters((prev) => ({
        ...prev,
        search_text: searchText,
        page_no: 1,
      }));
    } else {
      setSearchTerm("");
      setFilters((prev) => ({
        ...prev,
        search_text: "",
        page_no: 1,
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      handleApply();
    }
  };

  const resetSearch = () => {
    if (searchTerm.trim().length > 0) {
      setSearchTerm("");
      setFilters((prev) => ({
        ...prev,
        search_text: "",
        page_no: 1,
      }));
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setFilters((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 1)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleCustomerSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: searchText })
          .catch((err) => {});
      }, 1500);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilters({
      search_text: "",
      order_id: [],
      customer_id: [],
      page_no: 1,
      limit: 30,
      sort_by: "id",
      sort_type: SortType.DESC,
    });
    setSelectedFilters({
      search_text: "",
      order_id: [],
      customer_id: [],
      page_no: 1,
      limit: 30,
      sort_by: "id",
      sort_type: SortType.DESC,
    });
  };

  const handleApply = () => {
    setSelectedFilters((prev) => ({
      ...prev,
      search_text: searchTerm,
      order_id:
        filters.order_id?.map((item) => ({
          id: item.id,
          value: item.value,
        })) ?? [],
      customer_id:
        filters.customer_id?.map((item) => ({
          id: item.id,
          value: item.value,
        })) ?? [],
    }));
  };

  const fetchVouchers = async () => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_VOUCHERS])) {
      setIsLoading(true);
      voucherStore
        .getVouchers({
          page_no: selectedFilters.page_no ?? 1,
          limit: selectedFilters.limit ?? 30,
          sort_by: selectedFilters.sort_by ?? "id",
          sort_type: selectedFilters.sort_type ?? SortType.DESC,
          search_text: selectedFilters.search_text,
          order_id: selectedFilters.order_id?.map((item) => item.id) ?? [],
          customer_id:
            selectedFilters.customer_id?.map((item) => item.id) ?? [],
        })
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const downloadVouchers = async () => {
    if (userStore.hasPermission([EmployeeRoles.DOWNLOAD_VOUCHERS])) {
      setIsLoading(true);
      voucherStore
        .downloadVouchers({
          page_no: selectedFilters.page_no ?? 1,
          limit: selectedFilters.limit ?? 30,
          sort_by: selectedFilters.sort_by ?? "id",
          sort_type: selectedFilters.sort_type ?? SortType.DESC,
          search_text: selectedFilters.search_text,
          order_id: selectedFilters.order_id?.map((item) => item.id) ?? [],
          customer_id:
            selectedFilters.customer_id?.map((item) => item.id) ?? [],
        })
        .then((response) => {
          if (response.data) {
            const blob = new Blob([response.data], { type: "text/csv" });
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = response.filename
              ? response.filename
              : `VouchersReport.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
          } else {
            showError("Error", "Failed to download vouchers report csv file.");
          }
        })
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const syncAllBrands = async () => {
    if (userStore.hasPermission([EmployeeRoles.SYNC_ALL_VOUCHER_BRANDS])) {
      setIsLoading(true);
      voucherStore
        .syncAllBrands()
        .then(() => {
          showSuccess("All brands synced successfully.");
        })
        .catch((err) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const renderFilters = () => (
    <Box>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppSearchDropDown
            isMultiSelect={true}
            title="Select Order Id"
            value={filters.order_id ?? []}
            fontSize="12px"
            list={orderStore.orderIds.map((item) => {
              return { id: String(item), value: String(item) };
            })}
            local={false}
            handleSearch={handleOrderIdSearch}
            handleSelect={(items: FilterValue[]) => {
              setFilters((prev) => ({
                ...prev,
                order_id: items,
              }));
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <AppSearchDropDown
            isMultiSelect={true}
            title="Select Customers"
            value={filters.customer_id ?? []}
            fontSize="12px"
            list={customerStore.customers.map((item) => {
              return { id: String(item.id), value: item.name! };
            })}
            local={false}
            handleSearch={handleCustomerSearch}
            handleSelect={(items: FilterValue[]) => {
              setFilters((prev) => ({
                ...prev,
                customer_id: items,
              }));
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <AppButton
              variant="transparentPrimary"
              label="Clear"
              onClick={resetFilters}
              customStyles={{ minWidth: 120 }}
            />
            <AppButton
              variant="primary"
              label="Apply"
              onClick={handleApply}
              customStyles={{ minWidth: 120 }}
            />
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );

  const isActionsMenuOpen = Boolean(actionsMenuAnchor);
  const hasAnyActionPermission = userStore.hasPermission([
    EmployeeRoles.DOWNLOAD_VOUCHERS,
    EmployeeRoles.SYNC_ALL_VOUCHER_BRANDS,
  ]);
  const isActionsDisabled = !hasAnyActionPermission;

  const handleActionsMenuOpen = () => {
    if (!actionsAnchorRef.current) return;
    setActionsMenuAnchor(actionsAnchorRef.current);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (actionId: string) => {
    if (actionId === "download_vouchers") {
      downloadVouchers();
    }
    if (actionId === "sync_all_brands") {
      syncAllBrands();
    }
    handleActionsMenuClose();
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      {!userStore.hasPermission([EmployeeRoles.VIEW_VOUCHERS]) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">Vouchers</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage vouchers. Search by voucher number or product
                name.
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}
      {userStore.hasPermission([EmployeeRoles.VIEW_VOUCHERS]) && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">Vouchers</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage vouchers. Search by voucher number or product
                name.
              </CustomTypography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                gap: 1,
              }}
            >
              <AppButton
                variant="secondary"
                label={`View Voucher Requests`}
                onClick={() => {
                  navigate("/vouchers/requests");
                }}
                customStyles={{ minWidth: 180, height: 40 }}
              />
              {hasAnyActionPermission && (
                <Box ref={actionsAnchorRef}>
                  <AppButton
                    variant="grayscale"
                    label={`Actions`}
                    onClick={handleActionsMenuOpen}
                    disabled={isActionsDisabled}
                    customStyles={{ minWidth: 180 }}
                  />
                </Box>
              )}
            </Box>
            <Menu
              anchorEl={actionsMenuAnchor}
              open={isActionsMenuOpen}
              onClose={handleActionsMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              disableAutoFocusItem
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow:
                      "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                    minWidth: 240,
                    overflow: "hidden",
                  },
                },
              }}
            >
              {ACTION_OPTIONS.filter((option) => {
                if (option.id === "download_vouchers") {
                  return userStore.hasPermission(
                    EmployeeRoles.DOWNLOAD_VOUCHERS
                  );
                }
                if (option.id === "sync_all_brands") {
                  return userStore.hasPermission(
                    EmployeeRoles.SYNC_ALL_VOUCHER_BRANDS
                  );
                }
              }).map((option, index) => {
                return (
                  <MenuItem
                    key={option.id}
                    onClick={() => handleActionSelect(option.id)}
                    sx={{
                      fontSize: "0.9rem",
                      px: 2,
                      py: 1.25,
                      gap: 1,
                      borderTop:
                        index === 0
                          ? "none"
                          : "1px solid " + theme.palette.divider,
                      "&:hover": {
                        backgroundColor:
                          theme.palette.customColors?.lightPurple ||
                          theme.palette.action.hover,
                      },
                      "&.Mui-disabled": {
                        opacity: 0.4,
                      },
                    }}
                  >
                    <CustomTypography variant="body2" fontWeight={600}>
                      {option.value}
                    </CustomTypography>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isSearchExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">Search</CustomTypography>
              <IconButton size="small">
                {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                  }}
                >
                  <FormTextField
                    id="voucher-search"
                    placeholder="Search by voucher number, product name, or product code"
                    type="search"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(_, value) => handleSearchBar(value)}
                    onKeyDown={(e) => handleKeyDown(e)}
                  />
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Reset Search"
                    disabled={!searchTerm || searchTerm === ""}
                    onClick={resetSearch}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                </Box>
                {renderFilters()}
              </Box>
            </Collapse>
          </Paper>

          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              {voucherStore.vouchers.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10rem"
                  textAlign="center"
                  border="1px dashed"
                  borderColor="divider"
                  borderRadius={2}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={500}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No vouchers match the current search criteria.
                  </CustomTypography>
                </Box>
              )}
              {voucherStore.vouchers.length > 0 && (
                <VouchersTable
                  maxHeight={window.innerHeight - 350}
                  vouchers={voucherStore.vouchers}
                  limit={voucherStore.limit}
                  page_no={filters.page_no}
                  total_count={voucherStore.total_count}
                  total_pages={voucherStore.total_pages}
                  sortBy={filters.sort_by}
                  sortType={filters.sort_type}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={(column, sortType) => {
                    setFilters((prev) => ({
                      ...prev,
                      sort_by: column,
                      sort_type: sortType,
                    }));
                  }}
                />
              )}
            </Box>
          )}
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(VouchersListing);
